'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { ListingSummary } from '@/lib/feedbridge/types'
import { ListingGrid } from '@/components/listings/ListingGrid'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { renderHeading } from '@/utilities/renderHeading'

export interface TeamTabConfig {
  key: string
  label: string
}

interface TabData {
  listings: ListingSummary[]
  featuresMap: Record<string, Record<string, unknown>>
  underOfferMap: Record<string, boolean>
  thumbnailMap: Record<string, string>
  inspectionTimesMap: Record<string, string>
}

interface TeamListingsSectionProps {
  heading: string
  tabs: TeamTabConfig[]
  initialTabKey: string
  agentName: string
  agentEmail: string | null
  mode: 'agent' | 'all'
}

const VIEW_ALL_LINKS: Record<string, string> = {
  forSale: '/buy',
  sold: '/sold',
  forRent: '/rent',
  leased: '/leased',
}

/**
 * Staff profile listings section per Figma 1:14997.
 * Heading "{FirstName}'s Listings" (display-3xl) + segmented pill tabs
 * + "View All" outline button, with the same lazy client-side fetching
 * mechanics as LazyTabbedListings (/api/agent-listings).
 */
export function TeamListingsSection({
  heading,
  tabs,
  initialTabKey,
  agentName,
  agentEmail,
  mode,
}: TeamListingsSectionProps) {
  const [activeKey, setActiveKey] = useState(initialTabKey)
  const [tabDataCache, setTabDataCache] = useState<Record<string, TabData>>({})
  const [loading, setLoading] = useState(true)

  // Only show tabs that haven't loaded yet or have listings (same as LazyTabbedListings)
  const visibleTabs = tabs.filter((tab) => {
    const cached = tabDataCache[tab.key]
    return !cached || cached.listings.length > 0
  })

  const activeData = tabDataCache[activeKey]

  // Requests already in the air, keyed by tab. `tabDataCache` only fills in once a
  // response lands, so without this a tab can be requested twice before the first
  // response arrives — the canonical Tabs uses WAI-ARIA automatic activation, which
  // reports a selection from BOTH the focus and the click of a single pointer press.
  const inFlight = useRef<Set<string>>(new Set())

  const fetchTab = useCallback(
    async (tabKey: string, makeActive = true) => {
      if (tabDataCache[tabKey] || inFlight.current.has(tabKey)) {
        if (makeActive) setActiveKey(tabKey)
        return
      }

      if (makeActive) {
        setLoading(true)
        setActiveKey(tabKey)
      }
      inFlight.current.add(tabKey)
      try {
        const params = new URLSearchParams({
          tab: tabKey,
          mode,
          agentName,
          ...(agentEmail && { agentEmail }),
        })
        const res = await fetch(`/api/agent-listings?${params}`)
        const json = await res.json()

        setTabDataCache((prev) => ({
          ...prev,
          [tabKey]: {
            listings: json.data || [],
            featuresMap: json.featuresMap || {},
            underOfferMap: json.underOfferMap || {},
            thumbnailMap: json.thumbnailMap || {},
            inspectionTimesMap: json.inspectionTimesMap || {},
          },
        }))
      } catch {
        setTabDataCache((prev) => ({
          ...prev,
          [tabKey]: {
            listings: [],
            featuresMap: {},
            underOfferMap: {},
            thumbnailMap: {},
            inspectionTimesMap: {},
          },
        }))
      } finally {
        inFlight.current.delete(tabKey)
        if (makeActive) setLoading(false)
      }
    },
    [tabDataCache, mode, agentName, agentEmail],
  )

  // Fetch the first tab on mount
  const hasFetchedInitial = useRef(false)
  useEffect(() => {
    if (!hasFetchedInitial.current) {
      hasFetchedInitial.current = true
      fetchTab(initialTabKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If the active tab loaded empty, advance to the next not-yet-loaded tab
  useEffect(() => {
    const cached = tabDataCache[activeKey]
    if (cached && cached.listings.length === 0) {
      const next = tabs.find((t) => !tabDataCache[t.key])
      if (next) fetchTab(next.key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabDataCache, activeKey])

  // Hide the whole section when every tab has loaded empty
  const allLoadedEmpty =
    tabs.length > 0 &&
    tabs.every((tab) => tabDataCache[tab.key] && tabDataCache[tab.key].listings.length === 0)

  if (!tabs.length || allLoadedEmpty) return null

  const viewAllHref = VIEW_ALL_LINKS[activeKey] || '/buy'
  const showTabs = visibleTabs.length > 1

  const grid =
    loading || !activeData ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="aspect-[4/3] bg-muted" />
            <div className="mt-3 h-4 bg-muted rounded-sm w-3/4" />
            <div className="mt-2 h-3 bg-muted rounded-sm w-1/2" />
          </div>
        ))}
      </div>
    ) : (
      <ListingGrid
        listings={activeData.listings}
        featuresMap={activeData.featuresMap}
        underOfferMap={activeData.underOfferMap}
        thumbnailMap={activeData.thumbnailMap}
        inspectionTimesMap={activeData.inspectionTimesMap}
      />
    )

  return (
    // Figma Frame 453 desktop 4022:82119 (h 1400) wraps Frame 446 4022:82120 at y=96, and
    // tablet 4022:105722/105723 the same → section/md 96 top AND bottom from 768 up.
    // Mobile 4022:106498 (h 2859) wraps 4022:106499 at y=64 h=2731 → section/sm 64 both ends.
    <section className="mx-auto max-w-[var(--container-content)] px-[var(--spacing-container)] pt-[var(--spacing-section-sm)] pb-[var(--spacing-section-sm)] md:pt-[var(--spacing-section-md)] md:pb-[var(--spacing-section-md)] w-full">
      {/* Figma 4022:82119 — heading (56) / gap 24 / Frame 694 (4022:82123) holding the
          canonical `tabs` instance + a Mini outline `View All` button, gap/gap-2 = 8.
          Heading block → grid gap: desktop 4088:181705 h=104 → Frame 435 y=168 = 64 and
          tablet 4022:105724 h=96 → y=160 = 64, but mobile 4022:106500 h=88 → 4022:106505
          y=136 = 48. */}
      <Tabs
        className="flex flex-col gap-12 md:gap-16"
        hasPanels={showTabs}
        onValueChange={(value) => {
          void fetchTab(value)
        }}
        value={activeKey}
      >
        <div className="flex flex-col gap-6 items-start">
          {/* Headings/displaySm — text-display-3xl already carries the responsive
              40/44/48 scale (mobile text height 40 per 4022:106501), so no manual
              md: override. `*asterisks*` mark the Merriweather italic run (4022:82122). */}
          <h2 className="font-display text-display-3xl">{renderHeading(heading)}</h2>
          <div className="flex gap-2 items-center">
            {showTabs && (
              <TabsList aria-label="Listing type">
                {visibleTabs.map((tab) => (
                  // Figma sets the tab labels in caps at this instance (4022:82124);
                  // the canonical component deliberately does not force them.
                  <TabsTrigger className="uppercase" key={tab.key} value={tab.key}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            )}
            {/* 4022:82125 — icon-less `button`, type=outline, size=Mini (67×24). */}
            <Button asChild size="mini" variant="outline">
              <a href={viewAllHref}>View All</a>
            </Button>
          </div>
        </div>

        {/* Listing grid — the panel for whichever tab is active. */}
        {showTabs ? <TabsContent value={activeKey}>{grid}</TabsContent> : <div>{grid}</div>}
      </Tabs>
    </section>
  )
}
